// Critical-severity risk-alert banner (Q3 — DESIGN-DECISIONS-2026-05-01).
// Renders above page chrome until acknowledged. One banner at a time —
// most-recent unread Critical notification wins.

import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api.js";
import type { Notification } from "./NotificationsDrawer.js";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface Props {
  notification: Notification;
}

export function RiskCriticalBanner({ notification }: Props): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const patientId = (notification.payload?.["patient_id"] as string | undefined) ?? "";

  const acknowledge = useMutation({
    mutationFn: () => apiFetch(`/notifications/${notification._id}/read`, { method: "PATCH" }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["notifications"] }); },
  });

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="bg-red-600 text-white px-4 py-2 flex items-center justify-between gap-3 shrink-0 sticky top-0 z-30"
    >
      <div className="flex items-center gap-3 min-w-0">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" title={notification.title}>{notification.title}</p>
          <p className="text-xs opacity-90 truncate" title={notification.body}>
            {notification.body} · {relativeTime(notification.created_at)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {patientId && (
          <button
            onClick={() => navigate(`/patients/${patientId}`)}
            className="text-xs font-medium px-3 py-1 rounded-md bg-white/15 hover:bg-white/25 transition-colors"
          >
            View patient
          </button>
        )}
        <button
          onClick={() => acknowledge.mutate()}
          disabled={acknowledge.isPending}
          className="text-xs font-medium px-3 py-1 rounded-md bg-white text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
        >
          {acknowledge.isPending ? "…" : "Acknowledge"}
        </button>
      </div>
    </div>
  );
}
